import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Camera, ArrowLeft } from "@phosphor-icons/react";
import QRCode from "react-qr-code";
import GaleriaSocialCard from "@/components/GaleriaSocialCard";
import { CameraTypesBadge } from "@/components/CameraTypesBadge";
import axios from "axios";

const API = "/api";

const ERRORS_ES = {
  "Invalid email or password": "Correo o contraseña incorrectos",
  "Email already registered": "Este correo ya está registrado",
  "Token and password (min 6 chars) required": "Se requiere el enlace y una contraseña (mínimo 6 caracteres)",
  "Invalid or expired reset token": "El enlace de restablecimiento no es válido o expiró",
  "Reset token has expired": "El enlace de restablecimiento expiró",
};

function formatApiError(detail, lang) {
  const es = lang === "es";
  if (!detail) return es ? "Algo salió mal" : "Something went wrong";
  if (typeof detail === "string") return (es && ERRORS_ES[detail]) || detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login, register, user } = useAuth();
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    if (user && user !== false) navigate(redirectRef.current, { replace: true });
  }, [user, navigate]);

  // Where to send the user once authenticated. New registrants land on the
  // Task Cards page so they can grab the free field-guide booklet right away.
  const redirectRef = useRef("/");

  if (user && user !== false) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const form = new FormData(e.target);
    try {
      redirectRef.current = "/";
      await login(form.get("email"), form.get("password"));
      toast.success(lang === "es" ? "¡Bienvenido de nuevo!" : "Welcome back!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, lang));
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const form = new FormData(e.target);
    try {
      redirectRef.current = "/task-cards?welcome=booklet";
      await register(
        form.get("name"),
        form.get("email"),
        form.get("password")
      );
      toast.success(lang === "es" ? "¡Cuenta creada!" : "Account created!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, lang));
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, lang });
      if (data.reset_token) {
        // Production env may not have email configured yet — show the link directly so users can still reset.
        setResetToken(data.reset_token);
        toast.success(t("auth_reset_sent"));
      } else {
        toast.info(t("auth_reset_sent"));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, lang));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex" data-testid="login-page">
      <style>{`
        .login-left-panel { display: none; }
        .login-mobile-intro { display: block; }
        @media (min-width: 768px) {
          .login-left-panel { display: block !important; width: 50%; position: relative; }
          .login-mobile-intro { display: none !important; }
        }
      `}</style>
      <div className="login-left-panel">
        <img
          src="https://images.unsplash.com/photo-1631724618122-4f36ea2c7511?w=1200&q=80"
          alt="Camera lens"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="px-10 max-w-lg">
            <div className="flex items-center gap-3 mb-6">
              <Camera weight="fill" className="w-10 h-10 text-[#F5A524]" />
              <h1 className="font-['Outfit'] text-3xl font-semibold text-white tracking-tight">
                DSLR Advisor
              </h1>
            </div>
            <div className="mb-6">
              <CameraTypesBadge testId="login-camera-types-badge" />
            </div>

            <div className="mb-5">
              <div
                className="w-40 h-40 rounded-full border-2 border-[#F5A524]/40 overflow-hidden"
                style={{ shapeOutside: "circle(50%)", float: "left", marginRight: "1.25rem", marginBottom: "0.5rem" }}
                data-testid="author-photo"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_746e3f72-cf7b-4caa-8854-25292146b786/artifacts/hnm5wtqh_Hector%20Rivera%202.png"
                  alt="Hector Rivera"
                  className="w-full h-full object-cover"
                  style={{ transform: "scale(1.35)", objectPosition: "center 25%" }}
                />
              </div>
              <p className="text-[#D4D4D8] text-sm leading-relaxed">
                {lang === "es"
                  ? "Bienvenidos a DSLR Advisor — tu compañero inteligente para dominar la fotografía manual. Esta plataforma fue creada para estudiantes, principiantes y entusiastas de la fotografía que quieren entender mejor los ajustes de su cámara y mejorar su experiencia de disparo sin depender de las suposiciones. Ya sea que estés comenzando o continuando desarrollando tus habilidades, DSLR Advisor te ofrece guía práctica, escenarios del mundo real y recomendaciones personalizadas para ayudarte a capturar mejores imágenes con confianza."
                  : "Welcome to DSLR Advisor — your smart companion for mastering manual photography. This platform was created for students, beginners, and photography enthusiasts who want to better understand their camera settings and improve their shooting experience without relying on guesswork. Whether you're just starting out or continuing to develop your skills, DSLR Advisor provides practical guidance, real-world scenarios, and personalized recommendations to help you capture better images with confidence."}
              </p>
            </div>

            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
              {lang === "es"
                ? "Espero que disfrutes la experiencia y encuentres esta herramienta útil en tu viaje fotográfico. Tus comentarios son siempre bienvenidos mientras sigo mejorando la plataforma. Si deseas compartir tus opiniones o necesitas asistencia, no dudes en comunicarte por correo electrónico a dslradvisor@gmail.com."
                : "I hope you enjoy the experience and find this tool helpful in your photography journey. Your feedback is always welcome as I continue to improve the platform. If you would like to share your thoughts or need assistance, feel free to reach out via email at dslradvisor@gmail.com."}
            </p>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white font-['Outfit'] font-medium text-base tracking-tight">
                Hector Rivera
              </p>
              <p className="text-[#F5A524] text-xs uppercase tracking-[0.15em]">
                {lang === "es" ? "Fotógrafo Aficionado" : "Amateur Photographer"}
              </p>
            </div>

            <div
              className="mt-5 flex items-center gap-4 p-4 rounded-xl bg-[#F5A524]/5 border border-[#F5A524]/20"
              data-testid="login-qr-block"
            >
              <div className="shrink-0 bg-white p-2 rounded-lg">
                <QRCode
                  value={process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "https://dslradvisor.com")}
                  size={88}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#0A0A0A"
                  data-testid="login-qr-code"
                />
              </div>
              <div className="min-w-0">
                <p className="text-[#F5A524] text-[10px] uppercase tracking-[0.18em] font-semibold mb-1">
                  {lang === "es" ? "Instala en tu teléfono" : "Install on your phone"}
                </p>
                <p className="text-white text-sm font-medium leading-snug mb-1">
                  {lang === "es"
                    ? "Escanea para abrir DSLR Advisor en tu teléfono"
                    : "Scan to open DSLR Advisor on your phone"}
                </p>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">
                  {lang === "es"
                    ? "Luego: Compartir → Añadir a pantalla de inicio"
                    : "Then: Share → Add to Home Screen"}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <GaleriaSocialCard variant="compact" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-sm">
          <div className="login-mobile-intro text-center mb-6">
            <Camera weight="fill" className="w-10 h-10 text-[#F5A524] mx-auto mb-3" />
            <h1 className="font-['Outfit'] text-2xl font-semibold text-white mb-3">
              DSLR Advisor
            </h1>
            <div className="mb-4">
              <CameraTypesBadge testId="login-mobile-camera-types-badge" />
            </div>
            <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#F5A524]/40 mx-auto mb-3">
              <img
                src="https://customer-assets.emergentagent.com/job_746e3f72-cf7b-4caa-8854-25292146b786/artifacts/hnm5wtqh_Hector%20Rivera%202.png"
                alt="Hector Rivera"
                className="w-full h-full object-cover"
                style={{ transform: "scale(1.35)", objectPosition: "center 25%" }}
              />
            </div>
            <p className="text-[#A1A1AA] text-xs leading-relaxed max-w-xs mx-auto mb-2">
              {lang === "es"
                ? "Bienvenidos a DSLR Advisor — tu compañero inteligente para la fotografía manual. Creado para estudiantes y entusiastas que quieren mejorar sus ajustes de cámara y tomar mejores fotos sin suposiciones."
                : "Welcome to DSLR Advisor — your smart companion for manual photography. Created for students and enthusiasts who want to improve their camera settings and take better photos without guesswork."}
            </p>
            <p className="text-white text-sm font-medium">Hector Rivera</p>
            <p className="text-[#F5A524] text-[10px] uppercase tracking-widest">
              {lang === "es" ? "Fotógrafo Aficionado" : "Amateur Photographer"}
            </p>
          </div>

          <Tabs defaultValue="login" data-testid="auth-tabs">
            <TabsList className="w-full bg-[#121214] border border-white/10">
              <TabsTrigger
                value="login"
                className="flex-1 data-[state=active]:bg-[#F5A524] data-[state=active]:text-[#0A0A0A]"
                data-testid="login-tab"
              >
                {t("nav_login")}
              </TabsTrigger>
              <TabsTrigger
                value="register"
                className="flex-1 data-[state=active]:bg-[#F5A524] data-[state=active]:text-[#0A0A0A]"
                data-testid="register-tab"
              >
                {t("nav_register")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {showForgot ? (
                <div className="space-y-4 mt-6">
                  <button
                    onClick={() => { setShowForgot(false); setResetToken(null); }}
                    className="flex items-center gap-1 text-[#A1A1AA] hover:text-white text-sm transition-colors"
                    data-testid="back-to-login"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t("auth_back_to_login")}
                  </button>
                  <h3 className="font-['Outfit'] text-lg text-white">{t("auth_forgot_title")}</h3>
                  <p className="text-[#71717A] text-sm">{t("auth_forgot_subtitle")}</p>
                  {!resetToken ? (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="space-y-1.5">
                        <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                          {t("auth_email")}
                        </Label>
                        <Input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                          className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                          data-testid="forgot-email"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold"
                        data-testid="forgot-submit"
                      >
                        {loading ? t("loading") : t("auth_send_reset")}
                      </Button>
                    </form>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-emerald-400 text-sm">{t("auth_reset_sent")}</p>
                      <Button
                        onClick={() => navigate(`/reset-password?token=${resetToken}`)}
                        className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold"
                        data-testid="go-to-reset"
                      >
                        {t("auth_reset_link")}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4 mt-6">
                  <div className="space-y-1.5">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      {t("auth_email")}
                    </Label>
                    <Input
                      name="email"
                      type="email"
                      required
                      className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                      data-testid="login-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      {t("auth_password")}
                    </Label>
                    <Input
                      name="password"
                      type="password"
                      required
                      className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                      data-testid="login-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold active:scale-[0.98] transition-all"
                    data-testid="login-submit"
                  >
                    {loading ? t("loading") : t("auth_login_btn")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="w-full text-center text-[#A1A1AA] hover:text-[#F5A524] text-sm transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t("auth_forgot_password")}
                  </button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4 mt-6">
                <div className="space-y-1.5">
                  <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                    {t("auth_name")}
                  </Label>
                  <Input
                    name="name"
                    required
                    className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                    data-testid="register-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                    {t("auth_email")}
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    required
                    className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                    data-testid="register-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                    {t("auth_password")}
                  </Label>
                  <Input
                    name="password"
                    type="password"
                    required
                    minLength={6}
                    className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
                    data-testid="register-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold active:scale-[0.98] transition-all"
                  data-testid="register-submit"
                >
                  {loading ? t("loading") : t("auth_register_btn")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
